import { Instance } from 'mobx-state-tree';
import React, { useMemo } from 'react';
import { PharmacyInconsistencyHistory } from '../../../store/models/PharmacyInconsistencyHistory';
import { DataGrid } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';
import {
  FilterRow,
  SearchPanel,
  Button as DGButton,
  HeaderFilter,
  FilterPanel,
  GroupPanel,
  Grouping,
  ColumnChooser,
  Paging,
  Pager,
  Column,
  Export,
  DataGridTypes
} from 'devextreme-react/cjs/data-grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';

import { Translate } from '../../common/WithTranslate';
import { Page, TLCode } from '../../../common/enums';
import { PharmacyStatus } from '../../../store/models';
import moment from 'moment';
import { ApplicationStore, PharmacyStore } from '../../../store';

export interface DiscrepancyListProps {
  discrepancies: Array<Instance<typeof PharmacyInconsistencyHistory>>;
  pharmacyStatuses: Array<Instance<typeof PharmacyStatus>>;
  translate: Translate;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
}

interface DiscrepancyToDisplay {
  pharmacyInconsistencyHistoryId: number;
  pharmacyId: number;
  apbNumber: number;
  oldAfmpsValue: string | undefined;
  oldApbValue: string | undefined;
  newAfmpsValue: string | undefined;
  newApbValue: string | undefined;
  inconsistencyStartDate: string;
  inconsistencyEndDate: string | undefined;
  fieldType: string;
}

const STATUS_ID = 'StatusId';

export const DiscrepancyList = ({ discrepancies, pharmacyStatuses, translate, PharmacyStore, ApplicationStore }: DiscrepancyListProps) => {
  const onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Gardes-Groupe.xlsx');
      });
    });
  };

  const goToPharmacy = (e: any) => {
    const apbNumber = e.row.data.apbNumber;
    redirectToPharmacyPage(apbNumber);
  };

  const onRowClicked = (ev) => {
    const apbNumber = ev.data.apbNumber;
    redirectToPharmacyPage(apbNumber);
  };

  const redirectToPharmacyPage = (apbNumber: number) => {
    if (!PharmacyStore || !ApplicationStore) {
      return;
    }

    const ph = PharmacyStore.dataList.find((ph: any) => ph.apbNumber === apbNumber);

    if (!ph) {
      console.warn(`No pharmacy found with apb number: ${apbNumber}`);
      return;
    }

    const pharmacyId = ph.pharmacyId;
    ApplicationStore.navigate(`${Page.PharmacyPage}/${pharmacyId}`);
  };

  const dataSource = useMemo(() => {
    const displayableData = discrepancies.map((d) => {
      const fieldType = d.fieldType !== undefined ? d.fieldType.pharmacyFieldName : '';
      const isStatusFieldType = fieldType === STATUS_ID;
      return {
        ...d,
        fieldType,
        oldAfmpsValue:
          isStatusFieldType && d.oldAfmpsValue !== undefined
            ? pharmacyStatuses.find((ps) => ps.pharmacyStatusId === Number(d.oldAfmpsValue))!.name
            : d.oldAfmpsValue,

        oldApbValue:
          isStatusFieldType && d.oldApbValue !== undefined
            ? pharmacyStatuses.find((ps) => ps.pharmacyStatusId === Number(d.oldApbValue))!.name
            : d.oldApbValue,

        newAfmpsValue:
          isStatusFieldType && d.newAfmpsValue !== undefined
            ? pharmacyStatuses.find((ps) => ps.pharmacyStatusId === Number(d.newAfmpsValue))!.name
            : d.newAfmpsValue,

        newApbValue:
          isStatusFieldType && d.newApbValue !== undefined
            ? pharmacyStatuses.find((ps) => ps.pharmacyStatusId === Number(d.newApbValue))!.name
            : d.newApbValue,
      } as DiscrepancyToDisplay;
    });
    return new DataSource({
      store: { type: 'array', key: 'pharmacyInconsistencyHistoryId', data: displayableData },
      langParams: {
        locale: 'fr',
        collatorOptions: {
          sensitivity: 'base',
        },
      },
    });
  }, [discrepancies, pharmacyStatuses]);

  return (
    <DataGrid
      dataSource={dataSource}
      showBorders
      allowColumnReordering={true}
      rowAlternationEnabled={true}
      width="100%"
      allowColumnResizing={true}
      onExporting={onExporting}
      onRowClick={onRowClicked}
      onCellPrepared={(e) => {
        if (
          e.rowType === 'data' &&
          e.column.dataField === 'newAfmpsValue' &&
          e.data.newAfmpsValue !== e.data.oldAfmpsValue
        ) {
          e.cellElement.style.backgroundColor = '#f5b041';
        }

        if (e.rowType === 'data' && e.column.dataField === 'newApbValue' && e.data.newApbValue !== e.data.oldApbValue) {
          e.cellElement.style.backgroundColor = '#f5b041';
        }
      }}
    >
      <Export enabled={true} texts={{ exportAll: translate(TLCode.GRID_EXCEL_EXPORT) }} />
      <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
      <HeaderFilter visible={true} />
      <FilterRow visible={true} applyFilter={'auto'} />
      <FilterPanel visible={true} />
      <GroupPanel visible={true} />
      <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
      <Grouping autoExpandAll={false} />
      <ColumnChooser enabled={true} />
      <Paging defaultPageSize={20} />
      <Pager
        visible={true}
        allowedPageSizes={[5, 10, 20, 50, 100]}
        showPageSizeSelector={true}
        showInfo={true}
        showNavigationButtons={true}
      />
      <Column dataType="number" dataField="apbNumber" caption={translate(TLCode.PHARMACY_ApbNr)} />
      <Column dataType="string" dataField="fieldType" caption={translate(TLCode.DISCREPENCIES_FieldType)} />
      <Column dataType="string" dataField="oldAfmpsValue" caption={translate(TLCode.DISCREPENCIES_OldAfmps)} />
      <Column dataType="string" dataField="newAfmpsValue" caption={translate(TLCode.DISCREPENCIES_NewAfmps)} />
      <Column dataType="string" dataField="oldApbValue" caption={translate(TLCode.DISCREPENCIES_OldApb)} />
      <Column dataType="string" dataField="newApbValue" caption={translate(TLCode.DISCREPENCIES_NewApb)} />
      <Column
        dataType="string"
        dataField="inconsistencyStartDate"
        defaultSortOrder="desc"
        sortIndex={1}
        calculateCellValue={(data) =>
          data.inconsistencyStartDate === undefined
            ? ''
            : moment(new Date(data.inconsistencyStartDate)).format('DD/MM/YYYY')
        }
        calculateSortValue={(data) => new Date(data.inconsistencyStartDate)}
        caption={translate(TLCode.DISCREPENCIES_StartDate)}
      />
      <Column
        dataType="string"
        dataField="inconsistencyEndDate"
        calculateCellValue={(data) =>
          data.inconsistencyEndDate === undefined
            ? ''
            : moment(new Date(data.inconsistencyEndDate)).format('DD/MM/YYYY')
        }
        calculateSortValue={(data) => new Date(data.inconsistencyEndDate)}
        caption={translate(TLCode.DISCREPENCIES_EndDate)}
      />
      <Column
        caption="Actions"
        type="buttons">
        <DGButton
          hint={translate(TLCode.USER_Goto_Pharmacy)}
          icon={'home'}
          onClick={goToPharmacy}
          visible={true}
        />
      </Column>
    </DataGrid>
  );
};
